<template>
  <iColumn>
    <iColumn class="publication-header-wrapper" vertical-align="middle">
      <iOverlay
        :show-overlay-trigger="!canUploadLogo ? 'none' : isLoadingSplash ? 'always' :
          'hover' "
        class="splash-image"
        height="auto"
      >
        <iImageContainer
          :src="splashImage"
          whitespace-style="blur"
          :crop-if-needed="true"
          width="fill"
          class="splash-image-container"
          aspect-ratio="4/17"
        />
        <template #overlay>
          <iColumn
            align="center"
            height="fill"
            vertical-align="middle"
            width="fill"
          >
            <iLoadingSpinner v-if="isLoadingSplash" size="large" />
            <iColumn v-else width="hug">
              <iButton @click="$emit('click:splash-image')">
                Upload Splash
              </iButton>
            </iColumn>
          </iColumn>
        </template>
      </iOverlay>
      <iRow class="publication-header" vertical-align="middle" wrap="nowrap">
        <iColumn width="hug">
          <iOverlay
            :show-overlay-trigger="!canUploadLogo ?'none' : isLoadingLogo ? 'always' : 'hover' "
            height="auto"
            width="auto"
            :z-index-overlay="3"
          >
            <iImageContainer
              :src="logoImage"
              :height="$vssMobile ? 100 : 120"
              is-logo
              whitespace-style="whitespace"
              class="publication-header-logo"
            />
            <template #overlay>
              <iColumn
                align="center"
                height="fill"
                vertical-align="middle"
                width="fill"
              >
                <iLoadingSpinner v-if="isLoadingLogo" size="large" />
                <iColumn v-else width="hug">
                  <iButton @click="$emit('click:logo-image')">
                    Edit
                  </iButton>
                </iColumn>
              </iColumn>
            </template>
          </iOverlay>
        </iColumn>
        <iRow
          :wrap="wrapMenu ? 'wrap' : 'nowrap'"
          align="between"
          gap="small"
          vertical-align="middle"
        >
          <iColumn gap="none" width="hug">
            <!--            <iSpace :height="12" />-->
            <iRow
              v-if="canEditTitle"
              :max-width="500"
              vertical-align="middle"
              width="fill"
              wrap="nowrap"
            >
              <iTextEditInput
                v-model="internalTitle"
                edit-cta-style="icon"
                editable-on-click
                font-size="extraLarge"
                inline
                placeholder="Title"
                save-action-style="icon"
                @submit="saveTitle"
              />
            </iRow>
            <iRow v-else-if="title || $slots['no-edit-title']" :max-width="500" width="fill">
              <slot name="no-edit-title">
                <iSubHeading>
                  {{ title }}
                </iSubHeading>
              </slot>
            </iRow>
            <slot name="details">
              <iRow
                v-if="canEditDetails"
                :max-width="600"
                vertical-align="middle"
                wrap="nowrap"
              >
                <iTextEditInput
                  v-model="internalDetails"
                  edit-cta-style="icon"
                  editable-on-click
                  inline
                  placeholder="Description"
                  save-action-style="icon"
                  @submit="saveDetails"
                />
              </iRow>
              <iRow v-else-if="details" :max-width="500" width="fill">
                <iText variant="subtle">
                  {{ details }}
                </iText>
              </iRow>
            </slot>
            <slot v-if="$slots['below-details']" name="below-details" />
          </iColumn>
          <slot name="menu" />
        </iRow>
      </iRow>
    </iColumn>
    <iColumn class="main-content">
      <slot />
    </iColumn>
  </iColumn>
</template>

<script>
import { mapState } from "pinia";
import { useWindowStore } from "@vcl";

export default {
  name: "ProfileWrapper",
  props: {
    splashImage: {
      type: String,
      required: true,
    },
    isLoadingSplash: {
      type: Boolean,
      required: false,
      default: false,
    },
    logoImage: {
      type: String,
      required: true,
    },
    isLoadingLogo: {
      type: Boolean,
      required: false,
      default: false,
    },
    canUploadSplash: {
      type: Boolean,
      required: false,
      default: false,
    },
    canUploadLogo: {
      type: Boolean,
      required: false,
      default: false,
    },
    canEditTitle: {
      type: Boolean,
      required: false,
      default: false,
    },
    canEditDetails: {
      type: Boolean,
      required: false,
      default: false,
    },
    title: {
      type: String,
      required: false,
      default: "",
    },
    details: {
      type: String,
      required: false,
      default: "",
    },
    wrapMenu: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  emits: [
    "click:splash-image",
    "click:logo-image",
    "click:save-title",
    "click:save-details",
  ],
  data() {
    return {
      internalTitle: this.title,
      internalDetails: this.details,
    };
  },
  computed: {
    ...mapState(useWindowStore, [
      "$vssMobile",
    ]),
  },
  watch: {
    title(newTitle) {
      this.internalTitle = newTitle;
    },
    details(newDetails) {
      this.internalDetails = newDetails;
    },
  },
  created() {
    useWindowStore().init();
  },
  beforeUnmount() {
    useWindowStore().remove();
  },
  styleGuide: () => ({
    logoBorderWidth: { "size.border": "thick" },
    logoBorderColor: { "color.border": "dark" },
    logoBackground: { "color.background": "light" },
  }),
  methods: {
    saveTitle(title) {
      this.$emit("click:save-title", title);
    },
    saveDetails(details) {
      this.$emit("click:save-details", details);
    },
  },
};
</script>

<style lang="scss" scoped>
.publication-header-wrapper {
  position: relative;
}

.publication-header {
  position: relative;
  bottom: 30px;

  @include tablet {
    bottom: 21px;
  }
}

.publication-header-logo {
  z-index: 2;
}

.main-content {
  position: relative;
  top: -40px;
}

:deep(.splash-image-container) {
  height: 100%;
  aspect-ratio: 12/4;
  overflow: hidden;

  img {
    width: 100%;
  }
}

.profile-image-container {
  height: 100%;
  aspect-ratio: 4/4;
}

</style>
